<template>
  <div
    class="nav"
    v-if="!hide">
    <div>
      <router-link :to="hasEconomicLicense ? '/businessboard' : '/'">
        <img
          :src="
            getLogo(
              hasEconomicLicense
                ? 'bb-logo-colored.svg'
                : 'golfoffice-laying.svg'
            )
          "
          :class="hasEconomicLicense ? 'nav--logo-bb' : 'nav--logo'"
          :alt="
            hasEconomicLicense ? 'Business board logo' : 'Golf office logo'
          " />
      </router-link>
    </div>

    <div
      class="nav__client-container"
      v-if="showClientError">
      <div
        v-for="(client, i) in clientOptions"
        :key="i">
        <client-error
          :showTitle="true"
          :client="client" />
      </div>
    </div>
    <div
      class="nav--support"
      v-if="showSupport">
      <a
        :href="getSupportLink()"
        target="_blank">
        <use-svg svg="info-icon" />
        Guider och support
      </a>
    </div>
    <div
      class="nav--support_bb"
      v-if="showSupportBB">
      <a
        href="https://www.businessboard.se/support"
        target="_blank">
        <div class="bb-info-icon">?</div>
        Hjälp
      </a>
    </div>
    <div class="nav--club">
      <side-navigation position="header" />
    </div>
    <div
      class="nav--mobile"
      v-if="mq('to-small')">
      <button
        v-if="showMenu"
        @click="toggleMobileMenu"
        class="nav--mobile--button nav--mobile--close">
        <span class="visually-hidden">{{ $t('closeMenu') }}</span>
        X
      </button>
      <button
        v-else
        @click="toggleMobileMenu"
        class="nav--mobile--button">
        <span class="visually-hidden">{{ $t('openMenu') }}</span>
        <use-svg
          aria-hidden="true"
          svg="menu-icon" />
      </button>
      <div
        class="overlay"
        v-if="showMenu">
        <div class="overlay--content">
          <side-navigation @close="toggleMobileMenu"></side-navigation>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import UseSvg from '@/components/use-svg';
import SideNavigation from '@/components/navigation/side-navigation';
import mq from '@/mixins/mq.js';
import { BusinessBoardVersion } from '@/core/consts';
import ClientError from '@/components/clients/client-error';
import LocalizationMixin from '@/mixins/localization-mixin.vue';

const NavigationType = Object.freeze({
  OnTag: 'OnTag',
  OnService: 'OnService',
  CourseData: 'CourseData',
  BusinessBoard: 'Businessboard',
});

export default {
  name: 'MainNavigation',
  components: {
    UseSvg,
    SideNavigation,
    ClientError,
  },
  mixins: [mq, LocalizationMixin],
  props: {
    hide: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showMenu: false,
      isOnTag: false,
      isOnService: false,
      isCourseData: false,
      isBusinessBoard: false,
    };
  },
  computed: {
    businessBoardSettings() {
      return this.$store.getters['businessboard/settings'];
    },
    hasEconomicLicense() {
      if (!this.businessBoardSettings) {
        return false;
      }
      return (
        this.businessBoardSettings.businessBoardVersion ===
          BusinessBoardVersion.EconomicBasic ||
        this.businessBoardSettings.businessBoardVersion ===
          BusinessBoardVersion.EconomicPlus
      );
    },
    user() {
      return this.$store.getters['user/user'];
    },
    loggedIn() {
      return this.$store.getters['user/loggedIn'];
    },
    clients() {
      return this.$store.getters['clients/clients'];
    },
    clientOptions() {
      return this.$store.getters['clients/clientOptions'];
    },
    clubId() {
      return this.$store.getters['users/clubId'];
    },
    currentNavigationType() {
      if (this.isOnTag) {
        return NavigationType.OnTag;
      } else if (this.isOnService) {
        return NavigationType.OnService;
      } else if (this.isCourseData) {
        return NavigationType.CourseData;
      }
      return null;
    },
    showSupport() {
      const loggedInAndClubIdValid = this.loggedIn && this.clubId;

      const relevantRoute = !this.isBusinessBoard;

      return loggedInAndClubIdValid && relevantRoute;
    },
    showSupportBB() {
      const loggedInAndClubIdValid = this.loggedIn && this.clubId;

      const relevantRoute = this.isBusinessBoard;

      return loggedInAndClubIdValid && relevantRoute;
    },
    showClientError() {
      const loggedInAndClubIdValid = this.loggedIn && this.clubId;

      const clientOptionsValid =
        this.clientOptions &&
        this.clientOptions.length === 1 &&
        this.clientOptions.some(c => !c.status);

      return loggedInAndClubIdValid && clientOptionsValid;
    },
  },
  methods: {
    toggleMobileMenu() {
      this.showMenu = !this.showMenu;
    },
    getLogo(name) {
      let path = '';
      try {
        path = require(`../../assets/images/${name}`);
      } catch (e) {
        console.error(e);
      }
      return path;
    },
    checkUrlContains(keyword) {
      const url = this.$route.fullPath.toLowerCase();
      return url.includes(keyword.toLowerCase());
    },
    getSupportLink() {
      switch (this.currentNavigationType) {
        case NavigationType.OnTag:
          return 'https://support.golfoffice.se/se/ontag';
        case NavigationType.OnService:
          return 'https://support.golfoffice.se/se/onservice';
        default:
          return 'https://support.golfoffice.se';
      }
    },
  },
  watch: {
    $route() {
      this.isBusinessBoard = this.checkUrlContains('businessboard');
      this.isOnTag = this.checkUrlContains('ontag');
      this.isOnService = this.checkUrlContains('onservice');
      this.isCourseData = this.checkUrlContains('coursedata');
    },
    clubId(newValue) {
      this.$store.dispatch('clients/fetchAllClients');
    },
  },
};
</script>

<style lang="scss">
.nav {
  background-color: color(white);
  height: 4.475rem;
  box-shadow: 0 0.125rem 0.25rem 0 rgba(0, 0, 0, 0.3);
  display: grid;
  grid-template-columns: 263px 1fr auto auto;
  align-items: center;
  padding: 0 1rem;
  position: fixed;
  width: 100%;
  z-index: 9;

  &__client-container {
    display: flex;
    align-items: center;
    border-left: solid 6px rgb(190, 1, 1);
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    margin-right: 1rem;
    height: 70%;
    width: fit-content;
  }
}
.overlay {
  width: 100%;
  height: 100%;
  position: fixed;
  z-index: 99;
  left: 0;
  top: 4.375rem;
  .overlay--content {
    height: 100%;
    padding-bottom: 5rem;

    > div {
      height: 100%;
    }
    .side-navigation {
      height: 100%;
      width: 100%;
      position: relative;
      top: 0;
      width: 100%;

      @include media('to-small') {
        display: block;
        border-right: none;
        overflow-y: scroll;
      }
    }
  }
}
.nav--mobile {
  @include media('to-small') {
    display: flex;
    margin-left: auto;
  }
  @include media('small') {
    display: none;
  }
}
.nav--mobile--button {
  @include reset-button;
}
.nav--mobile--close {
  font-size: 2.5rem;
}
.nav--logo {
  height: 100%;
  width: 8rem;
  margin-right: 5rem;
}
.nav--logo-bb {
  height: 1.5rem;
  margin-top: 1.5rem;
  margin-right: 5rem;
}
.nav--club {
  display: flex;
  margin-left: auto;
  display: none;
  position: relative;
  align-items: center;
  white-space: nowrap;
  @include media('small') {
    display: flex;
  }
}
.nav--support {
  display: flex;
  display: none;
  position: relative;
  align-items: center;
  margin-left: auto;

  @include media('small') {
    display: flex;
  }

  a {
    display: flex;
    align-items: center;
    margin-right: 5rem;
    gap: 5px;
    white-space: nowrap;
  }

  svg {
    width: 20px;
    height: 20px;
  }
}

.nav--support_bb {
  display: flex;
  display: none;
  position: relative;
  align-items: center;
  margin-left: auto;

  @include media('small') {
    display: flex;
  }

  a {
    display: flex;
    align-items: center;
    margin-right: 5rem;
    gap: 5px;
    white-space: nowrap;
  }
  .bb-info-icon {
    background-color: color(bb-primary);
    color: white;
    font-size: 12px;
    padding-top: 2px;
    display: inline-block;
    margin-right: 0.5rem;
    margin-left: 0.5rem;
    border-radius: 50%;
    border: 1px solid white;
    width: 20px;
    height: 20px;
    text-align: center;
  }
}
</style>
